import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { ShieldWarning } from '@phosphor-icons/react'
import { Button, EmptyState, Spinner } from '@/ui'
import { useI18n } from '@/i18n'
import { useAuth } from './context'
import { hasAnyRole, type RoleName } from './roles'

/** A start-up wait longer than this is not slowness: the browser's storage has stopped answering. */
export const BOOT_STUCK_AFTER_MS = 10_000

/**
 * The start-up loader. Nothing it waits for (saved identity, this phone's registration) needs the
 * network, so if it is still spinning after BOOT_STUCK_AFTER_MS the browser's storage is stuck
 * (a known iPhone browser fault, worse with several tabs open). A reload does not clear that; the
 * person gets the steps that do, instead of a spinner forever.
 */
export function FullScreenSpinner({
  stuckAfterMs = BOOT_STUCK_AFTER_MS,
}: {
  stuckAfterMs?: number
}) {
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setStuck(true), stuckAfterMs)
    return () => clearTimeout(timer)
  }, [stuckAfterMs])
  return stuck ? <BootStuck /> : <BootSpinner />
}

function BootSpinner() {
  const { t } = useI18n()
  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper text-[28px] text-muted">
      <Spinner label={t('common.loading')} />
    </div>
  )
}

function BootStuck() {
  const { t } = useI18n()
  return (
    <div role="alert" className="flex min-h-dvh items-center justify-center bg-paper px-4 text-ink">
      <div className="flex max-w-md flex-col gap-4 text-[18px] leading-snug">
        <h1 className="text-[24px] font-semibold">{t('boot.stuck.title')}</h1>
        <p>{t('boot.stuck.body')}</p>
        <ol className="flex list-decimal flex-col gap-2 pl-6">
          <li>{t('boot.stuck.step1')}</li>
          <li>{t('boot.stuck.step2')}</li>
          <li>{t('boot.stuck.step3')}</li>
        </ol>
        <p className="font-semibold text-danger">{t('boot.stuck.warning')}</p>
        <Button size="touch" onClick={() => window.location.reload()}>
          {t('common.retry')}
        </Button>
      </div>
    </div>
  )
}

/** Requires an online identity (JWT). Sends anonymous visitors to /login?next=… */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status } = useAuth()
  const location = useLocation()
  if (status === 'loading') return <FullScreenSpinner />
  if (status === 'anonymous') {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }
  return children ? <>{children}</> : <Outlet />
}

/**
 * Role gate. Wrap a route element (or a section of a page):
 *   <RequireRole roles={[ROLES.PAYMENTS_APPROVER]}><PayoutsPage /></RequireRole>
 * `admin` / `central_admin` always pass.
 */
export function RequireRole({
  roles,
  children,
  fallback,
}: {
  roles: readonly RoleName[]
  children?: ReactNode
  fallback?: ReactNode
}) {
  const { roles: userRoles, status } = useAuth()
  const { t } = useI18n()
  if (status === 'loading') return <FullScreenSpinner />
  if (!hasAnyRole(userRoles, roles)) {
    return (
      <>
        {fallback ?? (
          <EmptyState
            icon={<ShieldWarning />}
            title={t('auth.forbidden')}
            description={roles.join(' · ')}
          />
        )}
      </>
    )
  }
  return children ? <>{children}</> : <Outlet />
}
